import { useEffect, useRef, useState } from "react";
import { GraduationCap, Flame, Loader2, Mic, PhoneOff } from "lucide-react";
import { api } from "@/lib/api";
import { READ_VOICES } from "@/components/VoiceControls";
import { chime } from "@/components/MiluWake";

const QUIET = 0.012;      // below this he takes it as silence
const HANG_ON = 1100;     // ms of quiet before he answers
const LEAST_SAID = 450;   // ms of speech before a turn counts
const LONGEST = 20000;    // ms: nobody talks for longer than this in one turn
const BUTT_IN = 0.055;    // louder than his own voice leaking back: the person is cutting in
const VOICE_KEY = "cml_call_voice";

// A hands-free call with MiLu: he listens, answers aloud, then listens again.
export const MiluCall = ({ sessionId, lang, onEnd, onLine, onRecap, onEyes, lessonLang = "",
                          compact = false }) => {
  const [state, setState] = useState("starting");  // starting | listening | thinking | talking
  const [lines, setLines] = useState([]);
  const [snag, setSnag] = useState("");
  const [voice, setVoice] = useState(() => localStorage.getItem(VOICE_KEY) || "nova");
  const chosen = useRef(voice);
  const [lesson_, setLesson] = useState(false);
  const [hard, setHard] = useState(false);
  const lesson = useRef("");
  const strict = useRef(false);
  const gear = useRef({});
  const alive = useRef(true);
  const feed = useRef(null);

  useEffect(() => {
    feed.current?.scrollTo({ top: feed.current.scrollHeight, behavior: "smooth" });
  }, [lines, state]);

  const remember = (who, text) => {
    setLines((was) => [...was, { who, text, id: `${who}-${was.length}` }]);
    onLine?.(who, text);
  };

  useEffect(() => {
    alive.current = true;

    const listen = async () => {
      const { recorder, meter } = gear.current;
      if (!alive.current || !recorder || recorder.state === "recording") return;
      setState("listening");
      const bits = [];
      recorder.ondataavailable = (e) => e.data.size && bits.push(e.data);
      recorder.onstop = async () => {
        clearInterval(gear.current.watch);
        if (!alive.current) return;
        const clip = new Blob(bits, { type: "audio/webm" });
        if (clip.size < 2000) return listen();          // a cough, not a sentence
        setState("thinking");
        try {
          const form = new FormData();
          form.append("audio", clip, "turn.webm");
          const { data: heard } = await api.post("/wingman/listen", form, { timeout: 60000 });
          const said = (heard.text || "").trim();
          if (!alive.current) return;
          if (said.length < 2) return listen();
          remember("me", said);
          const { data: reply } = await api.post("/wingman/chat", {
            session_id: sessionId, message: said, lang, mode: "call",
            lesson_lang: lesson.current, hard: strict.current,
          }, { timeout: 90000 });
          if (!alive.current) return;
          if (reply.kept) remember("note", `Noted — ${reply.kept}`);
          if (reply.eyes) {
            const [a, b] = String(reply.eyes).split(":");
            remember("note", b ? `Eyes for ${a}: ${b}` : `Eyes: ${a}`);
            onEyes?.(reply.eyes);
          }
          remember("milu", reply.say);
          setState("talking");
          const spoken = await api.post("/wingman/speak",
                                       { text: reply.say, voice: chosen.current },
                                       { responseType: "blob", timeout: 60000 });
          if (!alive.current) return;
          const audio = new Audio(URL.createObjectURL(spoken.data));
          gear.current.audio = audio;
          const carryOn = () => {
            clearInterval(gear.current.butt);
            if (alive.current) listen();
          };
          audio.onended = carryOn;
          audio.onerror = carryOn;
          await audio.play();
          // let the person cut in: if they speak over him, he stops and listens
          let over = 0;
          clearInterval(gear.current.butt);
          gear.current.butt = setInterval(() => {
            if (!alive.current || audio.paused) return;
            over = meter() > BUTT_IN ? over + 90 : 0;
            if (over >= 270) {
              clearInterval(gear.current.butt);
              audio.pause();
              listen();
            }
          }, 90);
        } catch {
          if (alive.current) {
            setSnag("That did not come through — say it again?");
            listen();
          }
        }
      };

      recorder.start();
      const began = Date.now();
      let spoke = 0;
      let hushed = 0;
      gear.current.watch = setInterval(() => {
        const loud = meter();
        const now = Date.now();
        if (loud > QUIET) {
          spoke += 90;
          hushed = 0;
        } else if (spoke > LEAST_SAID) {
          hushed += 90;
        }
        if ((hushed >= HANG_ON && spoke > LEAST_SAID) || now - began > LONGEST) {
          clearInterval(gear.current.watch);
          if (recorder.state === "recording") recorder.stop();
        }
      }, 90);
    };

    const pickUp = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });
        const ears = new (window.AudioContext || window.webkitAudioContext)();
        const gauge = ears.createAnalyser();
        gauge.fftSize = 512;
        ears.createMediaStreamSource(stream).connect(gauge);
        const frame = new Uint8Array(gauge.fftSize);
        gear.current = {
          stream, ears,
          recorder: new MediaRecorder(stream),
          meter: () => {
            gauge.getByteTimeDomainData(frame);
            let sum = 0;
            for (const v of frame) sum += ((v - 128) / 128) ** 2;
            return Math.sqrt(sum / frame.length);
          },
        };
        listen();
      } catch {
        setSnag("MiLu needs the microphone for a call — allow it and try again");
        setState("off");
      }
    };

    pickUp();
    return () => {
      alive.current = false;
      clearInterval(gear.current.watch);
      clearInterval(gear.current.butt);
      const { recorder, stream, ears, audio } = gear.current;
      if (recorder?.state === "recording") recorder.stop();
      audio?.pause();
      stream?.getTracks().forEach((t) => t.stop());
      ears?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const word = { starting: "Getting ready…", listening: "Listening…", thinking: "Thinking…",
                 talking: "MiLu is talking…", off: "Call ended" }[state];

  return (
    <div data-testid="milu-call" className="flex min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-white/10 px-5 py-2.5">
        <span
          data-testid="milu-call-state"
          data-state={state}
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] ${
            state === "listening" ? "bg-pink-500/15 text-pink-300"
              : state === "thinking" ? "bg-violet-500/15 text-violet-300"
              : state === "talking" ? "bg-emerald-500/15 text-emerald-300"
              : "bg-white/5 text-ink-soft"}`}
        >
          {state === "listening" ? <Mic className="h-3 w-3" /> : <Loader2 className={`h-3 w-3 ${
            state === "thinking" || state === "starting" ? "animate-spin" : ""}`} />}
          {word}
        </span>
        <span className="ms-auto" />
        {lessonLang && (
          <button
            data-testid="milu-call-lesson"
            onClick={() => {
              const on = !lesson_;
              setLesson(on);
              lesson.current = on ? lessonLang : "";
              if (!on) {
                setHard(false);
                strict.current = false;
              }
            }}
            title={`Hold the call in ${lessonLang}`}
            className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
              lesson_ ? "border-brand bg-brand/15 text-brand" : "border-white/15 text-ink-soft"}`}
          >
            <GraduationCap className="me-1 inline h-3 w-3" />
            {lessonLang.toUpperCase()}
          </button>
        )}
        {lessonLang && lesson_ && (
          <button
            data-testid="milu-call-hard"
            onClick={() => {
              const on = !hard;
              setHard(on);
              strict.current = on;
            }}
            title={`MiLu speaks only ${lessonLang.toUpperCase()} — no English at all`}
            className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
              hard ? "border-amber-400 bg-amber-500/15 text-amber-300"
                : "border-white/15 text-ink-soft"}`}
          >
            <Flame className="me-1 inline h-3 w-3" />
            Only {lessonLang.toUpperCase()}
          </button>
        )}
        <select
          data-testid="milu-call-voice"
          value={voice}
          onChange={(e) => {
            setVoice(e.target.value);
            chosen.current = e.target.value;
            localStorage.setItem(VOICE_KEY, e.target.value);
          }}
          title="His voice on calls"
          aria-label="His voice on calls"
          className="max-w-[6rem] rounded-full border border-white/15 bg-panel px-2 py-1 text-[11px] text-ink-soft outline-none hover:border-brand/50"
        >
          {READ_VOICES.map((v) => (
            <option key={v.id} value={v.id}>{v.label.split(" · ")[0]}</option>
          ))}
        </select>
        <button
          data-testid="milu-call-end"
          onClick={async () => {
            const heard = lines.length;
            chime("end");
            onEnd?.();
            if (heard < 2) return;
            try {
              const { data } = await api.post("/wingman/call/summary",
                                             { session_id: sessionId, lang,
                                               lesson_lang: lesson.current,
                                               hard: strict.current });
              if (data.recap) onRecap?.(data.recap, data.todos || [], data.lesson);
            } catch {
              /* the recap is a kindness, not a promise */
            }
          }}
          className="inline-flex items-center gap-1.5 rounded-full border border-destructive/50 px-3 py-1 text-[11px] text-destructive transition-colors hover:bg-destructive/10"
        >
          <PhoneOff className="h-3 w-3" /> Hang up
        </button>
      </div>

      <div ref={feed} data-testid="milu-call-transcript"
           className={`min-h-0 flex-1 space-y-2 overflow-y-auto px-5 py-3 ${
             compact ? "max-h-[30vh]" : "md:max-h-[42vh]"}`}>
        {lines.length === 0 && (
          <p className="text-xs text-ink-soft">Say hello — he is listening.</p>
        )}
        {lines.map((line) => (
          <p key={line.id} dir="auto"
             data-testid={line.who === "note" ? "milu-call-noted" : undefined}
             className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
               line.who === "me" ? "ms-auto bg-brand/15 text-ink"
                 : line.who === "note" ? "border border-brand/30 bg-brand/10 text-[11px] text-brand"
                 : "bg-panel text-ink"}`}>
            {line.text}
          </p>
        ))}
        {snag && <p className="text-[11px] text-amber-300">{snag}</p>}
      </div>
    </div>
  );
};
